import React from 'react';

import { tv } from '../../tv';

/**
 * The page scaffold: a column of regions, each owning the standard page
 * inset.
 *
 * This is the Card-slots idea at page altitude: REGIONS, not configuration.
 * Page.Header and Page.Content are places to put things, and what goes in
 * them is composition at the call site. There are deliberately no `heading`,
 * `description` or `actions` props: a config API has to grow a prop for
 * every arrangement a header can take (a breadcrumb above the title, a tab
 * row below it, two actions, none), and the product's own screens already
 * hold all of those. Thirty screens hand-write this same scaffold today,
 * each restating the inset and most drifting on it; the regions exist so the
 * inset is stated once, and ONLY the inset, because that is the part every
 * page agrees on.
 *
 * These regions are not the shared Header/Content slots. A slot reads its
 * inset from the container that claimed it, and that inset is a padding
 * step; the page inset is a different thing, responsive at the page's own
 * breakpoint and owned by no container. Sharing the components would mean
 * teaching the slots a second altitude, which is how one concept drifts into
 * two behind one name.
 */

const pageStyles = tv({
  slots: {
    root: 'ck:flex ck:w-full ck:flex-col',
    /* The one decision the regions carry: the standard page inset. 24px of
     * rhythm vertically; horizontally 16px on small screens, opening to 32px
     * once the viewport can afford it. Stated here and nowhere else, so a
     * screen cannot ship its own version of the page gutter. */
    header: 'ck:page-inset ck:py-6',
    /* Content separates itself from the page chrome above it with a
     * hairline. The rule lives on Content's own top edge rather than on
     * Header's bottom one, so it holds whatever precedes the body (a header,
     * a tab row, nothing at all) without each of those knowing to draw it. */
    content: 'ck:border-t ck:border-border-divider ck:page-inset ck:py-6',
  },
});

export interface PageProps {
  /** The page's regions: Page.Header and Page.Content. */
  children?: React.ReactNode;
  /** The element to render. Use `main` where the page is the main landmark. */
  as?: 'div' | 'main' | 'section';
  id?: string;
}

interface PageRegionProps {
  children?: React.ReactNode;
  id?: string;
}

export interface PageHeaderProps extends PageRegionProps {
  /** The element to render. Use `header` where the region is the page's banner. */
  as?: 'div' | 'header';
}

export interface PageContentProps extends PageRegionProps {
  /** The element to render. Use `section` where the region carries meaning. */
  as?: 'div' | 'section';
}

/** The top region of a page: title, description, actions, tabs, composed at the call site. */
export const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  { children, as: As = 'div', ...props },
  ref,
) {
  const { header } = pageStyles();
  return (
    <As
      {...props}
      // A polymorphic element has no single ref type across div/header, so
      // the cast is confined here rather than pushed onto callers.
      ref={ref as React.Ref<never>}
      className={header()}
      data-component="page-header"
    >
      {children}
    </As>
  );
});

/** The body of a page, divided from whatever sits above it. */
export const PageContent = React.forwardRef<HTMLElement, PageContentProps>(function PageContent(
  { children, as: As = 'div', ...props },
  ref,
) {
  const { content } = pageStyles();
  return (
    <As
      {...props}
      ref={ref as React.Ref<never>}
      className={content()}
      data-component="page-content"
    >
      {children}
    </As>
  );
});

const PageRoot = React.forwardRef<HTMLElement, PageProps>(function Page(
  { children, as: As = 'div', ...props },
  ref,
) {
  const { root } = pageStyles();
  return (
    <As {...props} ref={ref as React.Ref<never>} className={root()} data-component="page">
      {children}
    </As>
  );
});

/* The regions hang off the root because they are page-specific, unlike the
 * shared slots, which any container may claim: `Page.Header` cannot be
 * mistaken for the card slot at a call site, and the two never collide in
 * the barrel. */
export const Page = Object.assign(PageRoot, {
  Header: PageHeader,
  Content: PageContent,
});
